import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import type { ListDividendPayoutsResponse } from '@/features/tokenized-equity/grpc/types';

type UseFetchDividendPayoutsOptions = {
  recipient_address?: string;
  corporate_action_ids?: string[];
  dividend_types?: string[];
  page_size?: number;
  page_token?: string;
  enabled?: boolean;
  queryOptions?: Omit<
    UseQueryOptions<ListDividendPayoutsResponse, Error>,
    'queryKey' | 'queryFn' | 'enabled'
  >;
};

export const QUERY_KEY_DIVIDEND_PAYOUTS = (params: {
  recipient_address?: string;
  corporate_action_ids?: string[];
  dividend_types?: string[];
  page_size?: number;
  page_token?: string;
}) => [
  'dividendPayouts',
  params.recipient_address,
  params.corporate_action_ids,
  params.dividend_types,
  params.page_size,
  params.page_token,
];

export function useFetchDividendPayouts({
  recipient_address,
  corporate_action_ids,
  dividend_types,
  page_size = 50,
  page_token,
  enabled = true,
  queryOptions,
}: UseFetchDividendPayoutsOptions = {}) {
  return useQuery<ListDividendPayoutsResponse, Error>({
    queryKey: QUERY_KEY_DIVIDEND_PAYOUTS({
      recipient_address,
      corporate_action_ids,
      dividend_types,
      page_size,
      page_token,
    }),
    async queryFn() {
      const params = new URLSearchParams();
      if (recipient_address) params.set('recipient_address', recipient_address);
      if (corporate_action_ids?.length)
        params.set('corporate_action_ids', corporate_action_ids.join(','));
      if (dividend_types?.length) params.set('dividend_types', dividend_types.join(','));
      if (page_size) params.set('page_size', String(page_size));
      if (page_token) params.set('page_token', page_token);

      const response = await fetch(`/api/v1/dividendPayouts?${params}`);

      if (!response.ok) {
        let errorMsg = `HTTP error ${response.status}`;
        try {
          const errorData = (await response.json()) as { error?: string };
          if (errorData?.error) {
            errorMsg = errorData.error;
          }
        } catch {
          // ignore json parse error, keep default errorMsg
        }
        throw new Error(errorMsg);
      }

      const data = (await response.json()) as ListDividendPayoutsResponse;
      return data;
    },
    enabled,
    staleTime: 60_000,
    ...queryOptions,
  });
}
